import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, Trash2 } from 'lucide-react';

const getLetter = (p) => {
  if (p >= 90) return { g: 'A+', c: 'text-indgreen-700' };
  if (p >= 80) return { g: 'A', c: 'text-indgreen-600' };
  if (p >= 70) return { g: 'B', c: 'text-saffron-600' };
  if (p >= 60) return { g: 'C', c: 'text-saffron-700' };
  if (p >= 50) return { g: 'D', c: 'text-amber-600' };
  if (p >= 40) return { g: 'E', c: 'text-orange-700' };
  return { g: 'F', c: 'text-red-600' };
};

let id = 0;
const newItem = (name = '', score = '', weight = '') => ({ id: ++id, name, score, weight });

const GradeCalculator = () => {
  const [items, setItems] = useState([
    newItem('Assignment 1', 85, 20),
    newItem('Midterm Exam', 78, 30),
    newItem('Final Project', 92, 50),
  ]);

  const { weighted, total, percentage, letter } = useMemo(() => {
    let w = 0, t = 0;
    items.forEach(i => {
      const s = parseFloat(i.score);
      const wt = parseFloat(i.weight);
      if (!isNaN(s) && !isNaN(wt)) { w += s * wt; t += wt; }
    });
    const p = t > 0 ? w / t : 0;
    return { weighted: w, total: t, percentage: p, letter: getLetter(p) };
  }, [items]);

  const update = (itemId, field, value) => {
    setItems(items.map(i => i.id === itemId ? { ...i, [field]: value } : i));
  };
  const remove = (itemId) => setItems(items.filter(i => i.id !== itemId));
  const add = () => setItems([...items, newItem()]);

  return (
    <CalculatorLayout
      title="Grade Calculator"
      subtitle="Calculate weighted grades for courses, exams, and assignments."
      category="Other"
      description={<p>Add assessments with their score (%) and weight (%). The calculator outputs your weighted average and corresponding letter grade. Weights don't need to sum to 100 — the average is normalized automatically.</p>}
      formulaNote="Weighted Average = Σ(score × weight) / Σ(weight)"
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-6">
        <div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 text-xs">Assessment</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600 text-xs">Score %</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-600 text-xs">Weight %</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.id} className="border-t border-slate-100">
                    <td className="px-2 py-2">
                      <Input value={i.name} onChange={(e) => update(i.id, 'name', e.target.value)} placeholder="e.g. Midterm" className="h-9 border-slate-200" />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" value={i.score} onChange={(e) => update(i.id, 'score', e.target.value)} className="h-9 text-right border-slate-200" />
                    </td>
                    <td className="px-2 py-2">
                      <Input type="number" value={i.weight} onChange={(e) => update(i.id, 'weight', e.target.value)} className="h-9 text-right border-slate-200" />
                    </td>
                    <td className="px-1">
                      <Button variant="ghost" size="icon" onClick={() => remove(i.id)} className="h-8 w-8 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button variant="outline" onClick={add} className="mt-3"><Plus className="w-4 h-4 mr-1" /> Add Row</Button>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5 h-fit">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Final Grade</div>
          <div className="flex items-baseline gap-2 mt-1">
            <span className={`font-display text-5xl font-bold display-font ${letter.c}`}>{letter.g}</span>
            <span className="display-font text-2xl font-bold text-bharat-navy">{percentage.toFixed(2)}%</span>
          </div>
          <div className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Weighted Sum</span><span className="display-font font-semibold">{weighted.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Total Weight</span><span className="display-font font-semibold">{total}%</span></div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default GradeCalculator;
